import {
  type AuthenticationResult,
  BrowserAuthError,
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  InteractionRequiredAuthError,
  type PopupRequest,
  type SilentRequest,
} from '@azure/msal-browser';
import { addBreadcrumb, logger } from '@sentry/react';

import { backendApiRequest, loginRequest, msalConfig } from '@/config/auth/msalConfig';

/**
 * Nested app authentication (NAA) — Microsoft's recommended way for an Office
 * add-in to obtain a token, and the primary path for Connect Mailbox.
 *
 * The Office host brokers the request for the account the user is *already*
 * signed into Outlook with, so the usual sign-in ceremony disappears: no
 * dialog, no redirect chain through the tenant's identity provider, no MFA
 * re-entry. That also removes the failure this whole flow has been hardened
 * against — there is no window handle for a Cross-Origin-Opener-Policy header
 * to sever, because there is no second window.
 *
 * The token is the same one the dialog flow produces
 * (`api://<clientId>/access_as_user`), so the backend is unchanged: it still
 * verifies the identity token and exchanges it via OBO for the Graph
 * credential.
 *
 * Availability is a runtime question, not a manifest one — NAA is GA on
 * classic Outlook on Windows (2409+), new Outlook, Outlook on the web and
 * Outlook on Mac (16.89+), but a user on an older build must still get in.
 * `isNaaSupported` gates the attempt and every failure falls back to the
 * dialog flow, which remains fully supported.
 *
 * Requires the app registration to carry a Single Page Application redirect
 * URI of `brk-multihub://<add-in origin>` (origin only, no path).
 */

/** Whether this Office host can broker tokens for us. */
export function isNaaSupported(): boolean {
  try {
    return (
      typeof Office !== 'undefined' &&
      Office.context?.requirements?.isSetSupported('NestedAppAuth', '1.1') === true
    );
  } catch {
    // Hosts predating the requirement set can throw rather than return false.
    return false;
  }
}

let nestablePca: Promise<IPublicClientApplication> | null = null;

/**
 * The brokered MSAL instance. Created once; the factory performs its own
 * initialization, so callers get a ready instance. The promise (not the
 * resolved instance) is cached so overlapping callers share one creation
 * instead of racing the broker handshake.
 */
function getNestablePca(): Promise<IPublicClientApplication> {
  nestablePca ??= createNestablePublicClientApplication(msalConfig);
  return nestablePca;
}

/**
 * The account the host has already signed in, when MSAL exposes one. NAA does
 * not support `setActiveAccount`, so this reads rather than sets — and an
 * absent account is normal: the broker resolves the caller's identity itself.
 */
function brokeredAccount(pca: IPublicClientApplication) {
  return pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? undefined;
}

/**
 * True when the user deliberately backed out of a brokered prompt. Unlike the
 * popup flow this is trustworthy — the window belongs to the Office host, so
 * nothing but a real dismissal produces it, and re-prompting through another
 * surface would just fight the user.
 *
 * Both spellings are real: the NAA bridge maps a broker-window dismissal to
 * ClientAuthError `user_canceled`, while MSAL's own popup path uses
 * BrowserAuthError `user_cancelled`. AAD's `access_denied` is deliberately NOT
 * a cancellation — it is a server-side denial (Conditional Access, admin
 * consent required, or a declined consent screen), and treating it as "the
 * user changed their mind" hid persistent tenant-policy failures behind a
 * cancel message. Those fall back to the dialog flow, which surfaces the
 * provider's own error page.
 */
export function isNaaUserCancellation(error: unknown): boolean {
  const code = (error as { errorCode?: string } | null)?.errorCode;
  return code === 'user_canceled' || code === 'user_cancelled';
}

/**
 * Acquires the Connect Mailbox identity token through the host broker.
 *
 * Silent first; a brokered popup only when Microsoft says interaction is
 * genuinely required (first run, consent, a Conditional Access challenge).
 * Throws on anything else so the caller can fall back to the dialog flow —
 * except a user cancellation, which callers should honour rather than retry.
 */
export async function acquireMailboxTokenViaNaa(
  loginHint?: string,
): Promise<{ accessToken: string; email: string | null }> {
  const pca = await getNestablePca();
  const account = brokeredAccount(pca);

  const silentRequest: SilentRequest = {
    ...backendApiRequest,
    ...(account ? { account } : {}),
  };

  let result: AuthenticationResult;
  try {
    result = await pca.acquireTokenSilent(silentRequest);
    addBreadcrumb({ message: '[NAA] acquired the mailbox token silently' });
    logger.info('Connect mailbox: acquired the identity token silently via NAA');
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      throw error;
    }

    // Consent, first run, or a Conditional Access challenge. The host owns
    // this window, so `extraScopesToConsent` still gets Graph consent in the
    // same interaction the dialog flow used to.
    const popupRequest: PopupRequest = {
      ...backendApiRequest,
      extraScopesToConsent: loginRequest.scopes,
      ...(account ? { account } : {}),
      ...(loginHint ? { loginHint } : {}),
    };

    addBreadcrumb({ message: '[NAA] silent acquisition needs interaction — prompting via broker' });
    logger.info('Connect mailbox: NAA needs interaction, prompting through the host broker');
    result = await pca.acquireTokenPopup(popupRequest);
  }

  return { accessToken: result.accessToken, email: result.account?.username ?? null };
}

/**
 * Whether a failed NAA attempt is worth reporting as a defect.
 *
 * `popup_window_error` is documented: some older hosts advertise NAA but
 * cannot open the broker's `about:blank` window. It is an expected reason to
 * fall back, not a fault — and neither is a user cancellation.
 */
export function isExpectedNaaFallback(error: unknown): boolean {
  return (
    isNaaUserCancellation(error) ||
    (error instanceof BrowserAuthError && error.errorCode === 'popup_window_error')
  );
}
